import { z } from 'zod';
import { NodeMetricsSchema } from '../metrics/contracts';

export const NODE_TYPE_IDS = [
  'generic',
  'traffic-source',
  'social-network',
  'site',
  'landing-page',
  'form',
  'lead-magnet',
  'whatsapp',
  'email',
  'call',
  'chatbot',
  'crm',
  'sdr-seller',
  'meeting',
  'proposal',
  'payment',
  'sale',
  'post-sale',
  'upsell',
  'renewal',
  'churn',
  'condition',
  'delay',
  'automation',
  'webhook',
  'api',
  'ai-agent',
  'note',
  'group-frame',
] as const;

export const NODE_CATEGORIES = [
  'acquisition',
  'conversion',
  'nutrition',
  'commercial',
  'revenue',
  'retention',
  'automation',
  'integration',
  'logic',
  'documentation',
] as const;

export const NODE_STATUSES = ['planned', 'active', 'attention', 'problem', 'paused'] as const;
export const NODE_COLOR_OVERRIDES = ['neutral', 'blue', 'green', 'amber', 'violet'] as const;

export type NodeTypeId = (typeof NODE_TYPE_IDS)[number];
export type NodeCategory = (typeof NODE_CATEGORIES)[number];
export type NodeStatus = (typeof NODE_STATUSES)[number];
export type NodeColorOverride = (typeof NODE_COLOR_OVERRIDES)[number];

export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
] as const;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_NODE = 10;

export const AttachmentRefSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  mimeType: z.enum(ALLOWED_ATTACHMENT_MIME_TYPES),
  sizeBytes: z.number().int().nonnegative().max(MAX_ATTACHMENT_BYTES),
  createdAt: z.string().datetime(),
});

export type AttachmentRef = z.infer<typeof AttachmentRefSchema>;

export const NodeDataSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().max(500).optional(),
    category: z.enum(NODE_CATEGORIES),
    icon: z.string().min(1),
    colorOverride: z.enum(NODE_COLOR_OVERRIDES).optional(),
    url: z
      .string()
      .url()
      .refine(
        (value) => value.startsWith('https://') || value.startsWith('http://'),
        'Only HTTP(S) URLs are allowed.',
      )
      .optional(),
    responsible: z.string().max(120).optional(),
    notes: z.string().max(4000).optional(),
    status: z.enum(NODE_STATUSES).default('active'),
    attachments: z.array(AttachmentRefSchema).max(MAX_ATTACHMENTS_PER_NODE).default([]),
    /** SPEC-008 §76/T028/AC-063 — absent on older graphs; normalizes to `{}`. */
    metrics: NodeMetricsSchema,
  })
  .passthrough();

export type SemanticNodeData = z.infer<typeof NodeDataSchema>;

export function validateAttachmentInput(input: {
  mimeType: string;
  sizeBytes: number;
  currentCount: number;
}): string | null {
  if (
    !ALLOWED_ATTACHMENT_MIME_TYPES.includes(
      input.mimeType as (typeof ALLOWED_ATTACHMENT_MIME_TYPES)[number],
    )
  )
    return 'Unsupported attachment type.';
  if (input.sizeBytes > MAX_ATTACHMENT_BYTES) return 'Attachment exceeds the 10 MB limit.';
  if (input.sizeBytes <= 0) return 'Attachment is empty.';
  if (input.currentCount >= MAX_ATTACHMENTS_PER_NODE)
    return 'A node can have at most 10 attachments.';
  return null;
}

export function isKnownNodeType(type: string): type is NodeTypeId {
  return (NODE_TYPE_IDS as readonly string[]).includes(type);
}

export function isSafeNodeUrl(value: string, allowHttp = false): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || (allowHttp && url.protocol === 'http:');
  } catch {
    return false;
  }
}
